import { useState, useRef } from "react";
import { api } from "@/lib/api";
import { ScanLine, Upload, Loader2, X, Check, AlertTriangle, Wrench as WrenchIcon, Package } from "lucide-react";
import { toast } from "sonner";

const URGENCY_COLOR = {
  green: "bg-success/15 text-success border-success/30",
  amber: "bg-warning/15 text-warning border-warning/30",
  red: "bg-destructive/15 text-destructive border-destructive/30",
};

export default function VHCImportModal({ jobId, open, onClose, onImported }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [result, setResult] = useState(null); // { vehicle, items[] }
  const [picked, setPicked] = useState({}); // idx -> bool

  const reset = () => {
    setFile(null); setResult(null); setPicked({}); setAnalyzing(false); setAdding(false);
  };

  const close = () => { reset(); onClose?.(); };

  const choose = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f); setResult(null); setPicked({});
  };

  const analyze = async () => {
    if (!file) return;
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/jobs/${jobId}/vhc-import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      setResult(r.data);
      // pick all by default except low-score / unmatched
      const p = {};
      (r.data.items || []).forEach((it, i) => { p[i] = it.match_score >= 80; });
      setPicked(p);
      if ((r.data.items || []).length === 0) toast.warning("No items detected. Try a sharper photo or upload as PDF.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analysis failed");
    } finally { setAnalyzing(false); }
  };

  const addSelected = async () => {
    if (!result) return;
    const items = (result.items || [])
      .filter((_, i) => picked[i])
      .map(i => ({
        type: i.type || "labor",
        name: i.name,
        description: i.description || "",
        quantity: Number(i.quantity || 1),
        unit_price: Number(i.unit_price || 0),
        urgency: i.urgency || "amber",
        explainer_url: i.explainer_url || null,
      }));
    if (items.length === 0) { toast.warning("Pick at least one item"); return; }
    setAdding(true);
    try {
      await api.post(`/jobs/${jobId}/items/bulk`, items);
      toast.success(`${items.length} item${items.length !== 1 ? "s" : ""} added`);
      onImported?.(result.vehicle);
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add items");
    } finally { setAdding(false); }
  };

  const updateItem = (i, patch) => {
    setResult(r => ({ ...r, items: r.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4" onClick={close}>
      <div onClick={(e) => e.stopPropagation()} className="bg-card rounded-lg border border-border shadow-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto animate-fade-up" data-testid="vhc-modal">
        <div className="p-6 border-b border-border flex items-start justify-between">
          <div>
            <div className="label-overline text-primary">VHC Import</div>
            <h2 className="font-display text-2xl font-bold tracking-tight mt-1">Upload a Vehicle Health Check</h2>
            <p className="text-sm text-muted-foreground mt-1">Snap a photo or upload the PDF — the AI reads the sheet and pulls matching prices from your catalog.</p>
          </div>
          <button onClick={close} className="text-muted-foreground hover:text-foreground p-1" data-testid="vhc-close-btn"><X className="w-5 h-5" /></button>
        </div>

        {!result && (
          <div className="p-6">
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-border rounded-lg p-10 text-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all"
              data-testid="vhc-dropzone"
            >
              <ScanLine className="w-10 h-10 mx-auto text-muted-foreground" />
              <div className="font-display font-bold text-lg mt-4">{file ? file.name : "Tap to upload or scan"}</div>
              <div className="text-sm text-muted-foreground mt-1">JPG, PNG or PDF · up to 4 pages</div>
              <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" className="hidden" onChange={choose} data-testid="vhc-file-input" />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={close} className="btn-outline" data-testid="vhc-cancel-btn">Cancel</button>
              <button onClick={analyze} disabled={!file || analyzing} className="btn-primary" data-testid="vhc-analyze-btn">
                {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {analyzing ? "Analysing…" : "Analyse VHC"}
              </button>
            </div>
            {analyzing && <p className="text-xs text-muted-foreground mt-3 text-center">Reading sheet and matching to catalog — usually 5-15 seconds.</p>}
          </div>
        )}

        {result && (
          <div className="p-6">
            {result.vehicle && (
              <div className="rounded-lg border border-border bg-muted/30 p-4 mb-5">
                <div className="label-overline">Detected vehicle</div>
                <div className="mt-1 font-semibold">
                  {[result.vehicle.year, result.vehicle.make, result.vehicle.model].filter(Boolean).join(" ") || "—"}
                </div>
                <div className="text-xs font-mono-plex text-muted-foreground">
                  {result.vehicle.registration ? `Reg ${result.vehicle.registration}` : ""}
                  {result.vehicle.mileage ? ` · ${result.vehicle.mileage.toLocaleString()} miles` : ""}
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mb-3">
              <div className="label-overline">Suggested items ({(result.items || []).length})</div>
              <div className="text-xs text-muted-foreground">Tick items to add. Edit prices if needed.</div>
            </div>

            <ul className="divide-y divide-border border border-border rounded-lg overflow-hidden">
              {(result.items || []).map((it, i) => (
                <li key={i} className={`p-4 ${picked[i] ? "" : "opacity-50"}`} data-testid={`vhc-suggestion-${i}`}>
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={!!picked[i]}
                      onChange={(e) => setPicked(p => ({ ...p, [i]: e.target.checked }))}
                      className="mt-1 w-4 h-4 accent-primary"
                      data-testid={`vhc-pick-${i}`}
                    />
                    <div className={`w-9 h-9 rounded-md grid place-items-center shrink-0 ${
                      it.urgency === "green" ? "bg-success/20 text-success" :
                      it.urgency === "amber" ? "bg-warning/20 text-warning" :
                      "bg-destructive/20 text-destructive"
                    }`}>
                      {it.type === "part" ? <Package className="w-4 h-4" /> : <WrenchIcon className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <input
                          value={it.name}
                          onChange={(e) => updateItem(i, { name: e.target.value })}
                          className="bg-transparent font-semibold flex-1 min-w-0 outline-none border-b border-transparent focus:border-primary"
                          data-testid={`vhc-name-${i}`}
                        />
                        <span className={`text-xs font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${URGENCY_COLOR[it.urgency]}`}>
                          {it.urgency === "red" && <AlertTriangle className="w-3 h-3 inline mr-0.5" />}
                          {it.urgency}
                        </span>
                        {it.price_source === "toyota_trp" ? (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/10 text-red-600 border border-red-500/30" data-testid={`vhc-trp-badge-${i}`} title={`Official Toyota fixed repair price · ${it.trp_class || ""}`}>
                            <Check className="w-3 h-3 inline mr-0.5" /> Toyota price
                          </span>
                        ) : it.matched_catalog_id ? (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary border border-primary/30">
                            <Check className="w-3 h-3 inline mr-0.5" /> catalog match
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border">
                            no match — set price manually
                          </span>
                        )}
                      </div>
                      {it.description && (
                        <input
                          value={it.description}
                          onChange={(e) => updateItem(i, { description: e.target.value })}
                          className="input-default text-sm py-1.5"
                          data-testid={`vhc-desc-${i}`}
                        />
                      )}
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-muted-foreground">qty</span>
                        <input
                          type="number" step="0.1" min="0" value={it.quantity}
                          onChange={(e) => updateItem(i, { quantity: e.target.value })}
                          className="input-default w-20 py-1 text-sm"
                          data-testid={`vhc-qty-${i}`}
                        />
                        <span className="text-muted-foreground">×</span>
                        <span className="text-muted-foreground">£</span>
                        <input
                          type="number" step="0.01" min="0" value={it.unit_price}
                          onChange={(e) => updateItem(i, { unit_price: e.target.value })}
                          className="input-default w-24 py-1 text-sm"
                          data-testid={`vhc-price-${i}`}
                        />
                        <span className="ml-auto font-mono-plex font-semibold">£{(Number(it.quantity) * Number(it.unit_price)).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="flex justify-between items-center mt-5">
              <button onClick={reset} className="btn-outline" data-testid="vhc-redo-btn">
                <Upload className="w-4 h-4" /> Re-upload
              </button>
              <div className="flex gap-2">
                <button onClick={close} className="btn-outline" data-testid="vhc-skip-btn">Skip</button>
                <button onClick={addSelected} disabled={adding} className="btn-primary" data-testid="vhc-add-btn">
                  {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Add selected
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
